import React, { useMemo } from "react";
import { Platform, useWindowDimensions } from "react-native";
import type { IMessage } from "react-native-gifted-chat";

import MessageBubbleView from "./MessageBubbleView";

import { useChatTheme } from "@/features/knowledge-graph/hooks/useChatTheme";
import { bubbleMaxWidth } from "@/features/knowledge-graph/utils/bubbleMaxWidth";
import { useTheme } from "@/providers/ThemeProvider";
import { TEST_ID_GEMINI_CHAT } from "e2e/testId";

/**
 * Extended message metadata for chat messages with custom properties.
 */
interface MessageMetadata {
  researchMode?: boolean;
  playerIds?: string[];
  citationLinks?: string[];
}

/**
 * Extended IMessage type that includes custom metadata field.
 */
interface ExtendedMessage extends IMessage {
  metadata?: MessageMetadata;
}

/**
 * Props for MessageBubbleContainer component
 */
interface MessageBubbleContainerProps {
  message: ExtendedMessage;
  previousMessage?: ExtendedMessage;
  nextMessage?: ExtendedMessage;
}

/**
 * MessageBubbleContainer - Container component for message bubble
 * @param root0 - Component props
 * @param root0.message - Current message
 * @param root0.previousMessage - Previous message in chat
 * @param root0.nextMessage - Next message in chat
 */
const MessageBubbleContainer: React.FC<MessageBubbleContainerProps> = ({
  message,
  previousMessage,
  nextMessage,
}) => {
  const { colors } = useChatTheme();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { width: windowWidth } = useWindowDimensions();

  const aiUserId = "ai";

  // Safety check for message structure
  const isValidMessage = useMemo(() => !!(message && message.user), [message]);

  // Check if this is a progress message
  const isProgressMessage = useMemo(
    () => message?.user?._id === "progress",
    [message.user?._id]
  );

  // Calculate iMessage-like spacing - with null safety
  const spacingAndLayout = useMemo(() => {
    const isSameUser = previousMessage?.user?._id === message?.user?._id;
    const isNextSameUser = nextMessage?.user?._id === message?.user?._id;
    const isAIMessage = message?.user?._id === aiUserId;
    const isPreviousUser = previousMessage?.user?._id !== aiUserId;
    const isFirstAIResponse = isAIMessage && isPreviousUser && !isSameUser;

    // iMessage spacing logic:
    // - 2px between consecutive messages from same user
    // - 8px between messages from different users
    // - 4px margin top for first message in a group
    // - Reduced spacing between first user message and first AI response
    const marginTop = isSameUser ? 1 : isFirstAIResponse ? 2 : 4;
    const marginBottom = isNextSameUser ? 1 : 8;

    // Responsive maxWidth — shared with the desktop GiftedChat renderer in
    // ChatContainer so both message paths cap bubbles identically (SE-7084).
    const maxWidth = bubbleMaxWidth(windowWidth);

    // Responsive margins that scale with screen size
    const horizontalMargin = Math.max(6, windowWidth * 0.02);

    return {
      marginTop,
      marginBottom,
      marginLeft: (isAIMessage ? horizontalMargin : "auto") as number | "auto",
      marginRight: (isAIMessage ? "auto" : horizontalMargin) as number | "auto",
      maxWidth,
      isAIMessage,
    };
  }, [
    previousMessage?.user?._id,
    message?.user?._id,
    nextMessage?.user?._id,
    windowWidth,
  ]);

  const roleTestID = spacingAndLayout.isAIMessage
    ? TEST_ID_GEMINI_CHAT.AI_MESSAGE
    : TEST_ID_GEMINI_CHAT.USER_MESSAGE;

  const roleLabel: "ai" | "user" = spacingAndLayout.isAIMessage ? "ai" : "user";

  // Check if research mode was active for this message
  const hasResearchMode = useMemo(
    () => !!message.metadata?.researchMode,
    [message]
  );

  const isAndroid = Platform.OS === "android";

  if (!isValidMessage) {
    console.warn("Message or message.user is undefined:", message);
    return null;
  }

  return (
    <MessageBubbleView
      message={message}
      colors={colors}
      isDark={isDark}
      isProgressMessage={isProgressMessage}
      spacingAndLayout={spacingAndLayout}
      hasResearchMode={hasResearchMode}
      isAndroid={isAndroid}
      roleTestID={roleTestID}
      roleLabel={roleLabel}
    />
  );
};

export default MessageBubbleContainer;
